import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Slideshow } from "@/components/Slideshow";
import { useLanguage, localizedPath } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { thumbUrl, altFor, srcSet } from "@/lib/images";
import { ART_LABEL, isCategorySeries } from "@/lib/categories";

export default function ProjectDetail() {
  const { slug } = useParams();
  const decoded = decodeURIComponent(slug || "");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?preview=1: Admin-Vorschau, lädt auch Entwürfe (per Login-Cookie)
  const isPreview = searchParams.get("preview") === "1";
  const [works, setWorks] = useState([]);
  const [projectName, setProjectName] = useState(decoded);
  const [meta, setMeta] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [view, setView] = useState("grid");
  const [locked, setLocked] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const { t, lang } = useLanguage();

  // Anzeigename: EN nutzt name_en aus den Serien-Meta-Daten; Serien, die wie
  // eine Rubrik heißen, bekommen die Rubrik-Beschriftung im Plural
  const displayTitle =
    meta && meta.art && isCategorySeries(projectName)
      ? t(ART_LABEL[meta.art])
      : lang === "en"
        ? (meta && meta.name_en) || t(projectName)
        : projectName;

  useSeo({
    title:
      lang === "en"
        ? `${displayTitle} · Wim Jansen Delgado · Photographer Hamburg`
        : `${displayTitle} · Wim Jansen Delgado · Fotograf Hamburg`,
    description:
      lang === "en"
        ? `Photo series “${displayTitle}” by Wim Jansen Delgado, photographer based in Hamburg.`
        : `Fotoserie „${displayTitle}“ von Wim Jansen Delgado, Fotograf aus Hamburg.`,
  });

  // Lädt die Serie; passwortgeschützte Serien brauchen ein Freischalt-Token
  const loadProject = useCallback(() => {
    setLoading(true);
    setIndex(0);
    setView("grid");
    const token = sessionStorage.getItem(`wj_unlock_${decoded}`);
    const suffix = !isPreview && token ? `?token=${encodeURIComponent(token)}` : "";
    api
      .get(`/${isPreview ? "admin/" : ""}projects/${encodeURIComponent(decoded)}${suffix}`)
      .then((res) => {
        setWorks(res.data.works || []);
        if (res.data.name) setProjectName(res.data.name);
        setMeta(res.data.meta || null);
        setLocked(false);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          // Passwortgeschützt: ruhiges Eingabefeld statt Weiterleitung
          setLocked(true);
          setWorks([]);
        } else {
          // Gelöschte oder unveröffentlichte Serie: zurück zur Übersicht
          navigate(localizedPath("/projekte", lang), { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [decoded]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Zurück zur Übersicht: Grid zeigen und zur zuletzt gesehenen Kachel scrollen
  const pendingScroll = useRef(null);
  const backToOverview = useCallback(() => {
    pendingScroll.current = index + 1;
    setView("grid");
  }, [index]);

  useEffect(() => {
    if (view !== "grid" || pendingScroll.current == null) return;
    const el = document.querySelector(`[data-testid="overview-thumb-${pendingScroll.current}"]`);
    if (el) el.scrollIntoView({ block: "center" });
    pendingScroll.current = null;
  }, [view]);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setPwBusy(true);
    setPwError(false);
    try {
      const res = await api.post(`/projects/${encodeURIComponent(decoded)}/unlock`, {
        password: pw.trim(),
      });
      sessionStorage.setItem(`wj_unlock_${decoded}`, res.data.token);
      setPw("");
      loadProject();
    } catch {
      setPwError(true);
    } finally {
      setPwBusy(false);
    }
  };

  // Serienliste für „Nächstes Projekt" (Reihenfolge wie in der Übersicht)
  useEffect(() => {
    api
      .get("/projects")
      .then((res) => setAllProjects(res.data || []))
      .catch(() => setAllProjects([]));
  }, []);

  // Fokus-Modus: in der Slideshow Kopf- und Fußzeile der Seite ausblenden,
  // sichtbar bleiben nur Bild, Unterschrift, Pfeile und der Umschalter
  useEffect(() => {
    document.body.classList.toggle("focus-slideshow", view === "slides");
    return () => document.body.classList.remove("focus-slideshow");
  }, [view]);

  // Direkteinstieg von der Projekte-Seite: ?bild=3 öffnet die Slideshow am 3. Bild
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || locked || works.length === 0) return;
    deepLinkDone.current = true;
    const b = parseInt(searchParams.get("bild") || "", 10);
    if (b >= 1 && b <= works.length) {
      setIndex(b - 1);
      setView("slides");
    }
  }, [works, locked, searchParams]);

  const projectOrt = meta ? (lang === "en" ? meta.ort_en : meta.ort) : "";
  const current = works[index];
  // Daten für die geteilte Slideshow (gleiche wie in der Rubrik-Slideshow)
  const slides = works.map((w) => {
    const imgOrt = lang === "en" ? w.ort_en || "" : w.ort || "";
    const ort = imgOrt || projectOrt;
    return {
      key: w.id,
      image: w.image,
      title: w.title || displayTitle,
      ort,
      note: lang === "en" ? w.note_en || w.note || "" : w.note || "",
      alt: altFor(w, lang, { name: displayTitle, ort }),
      orientation: w.orientation,
    };
  });

  // Meta-Zeile und Einleitung in der Sprache der Adresse — ort_en ohne DE-Fallback
  const context = meta ? (lang === "en" ? meta.context_en || meta.context_de : meta.context_de) : "";
  const intro = meta ? (lang === "en" ? meta.intro_en || meta.intro_de : meta.intro_de) : "";
  const metaParts = [projectOrt, context].filter(Boolean);

  const myIndex = allProjects.findIndex((p) => p.slug === decoded);
  const nextProject = myIndex >= 0 ? allProjects[(myIndex + 1) % allProjects.length] : null;

  return (
    <div data-testid="project-detail-page">
      <section
        className={`relative mx-auto max-w-7xl px-4 md:px-12 lg:px-16 ${
          view === "slides"
            ? "flex min-h-[100dvh] flex-col justify-center pb-8 pt-6"
            : "pb-16 pt-20 md:pt-24"
        }`}
      >
        {/* Passwortgeschützte Serie: bewusst ohne Titel und Hinweise */}
        {locked && !loading ? (
          <div className="flex min-h-[45vh] items-center justify-center" data-testid="project-unlock-gate">
            <form onSubmit={submitPassword} className="w-full max-w-xs text-center">
              <Lock size={20} strokeWidth={1.5} className="mx-auto text-smoke" />
              <label htmlFor="project-password" className="sr-only">
                {t("Passwort")}
              </label>
              <input
                id="project-password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={t("Passwort")}
                autoFocus
                autoComplete="off"
                data-testid="project-password-input"
                className="mt-8 w-full border-b border-black/25 bg-transparent py-3 text-center font-mono text-sm tracking-[0.25em] text-bone outline-none transition-colors focus:border-ember-deep"
              />
              {pwError && (
                <p
                  data-testid="project-unlock-error"
                  className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ember-deep"
                >
                  {t("Das Passwort stimmt nicht.")}
                </p>
              )}
              <button
                type="submit"
                disabled={pwBusy}
                data-testid="project-unlock-btn"
                className="mt-8 inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-10 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember-deep disabled:opacity-50"
              >
                {t("Ansehen")}
              </button>
            </form>
          </div>
        ) : (
          <>
        {view === "grid" && (
        <Reveal>
          <Link
            to={localizedPath("/projekte", lang)}
            data-testid="project-back-link"
            className="group mb-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-smoke transition-colors duration-300 hover:text-ember-deep"
          >
            <ArrowLeft
              size={13}
              className="transition-transform duration-300 group-hover:-translate-x-1.5"
            />
            {t("Alle Projekte")}
          </Link>
        </Reveal>
        )}

        {view === "grid" && (
        <Reveal delay={0.05}>
          <h1
            data-testid="project-detail-title"
            className="font-serif text-4xl font-light tracking-tight text-bone md:text-5xl"
          >
            {t(displayTitle)}
          </h1>
        </Reveal>
        )}
        {view === "grid" && metaParts.length > 0 && (
          <Reveal delay={0.1}>
            <p
              className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke"
              data-testid="project-meta-line"
            >
              {metaParts.join(" · ")}
            </p>
          </Reveal>
        )}
        {view === "grid" && intro && (
          <Reveal delay={0.15}>
            <p
              className="mt-6 max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg"
              data-testid="project-intro"
            >
              {intro}
            </p>
          </Reveal>
        )}

        {loading ? (
          <div className="mt-12 h-[50vh] animate-pulse bg-panel" />
        ) : works.length === 0 ? (
          <p className="mt-12 font-mono text-sm font-light text-smoke" data-testid="project-empty">
            {t("Dieses Projekt hat noch keine Bilder.")}
          </p>
        ) : (
          current && (
            <div className="mt-8" data-testid="project-slideshow">
              {view === "grid" && (
              <div className="mb-4 flex flex-none justify-end">
                <button
                  type="button"
                  data-testid="slideshow-overview-toggle"
                  onClick={() => setView("slides")}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors duration-300 hover:text-ember-deep"
                >
                  {t("Slideshow")}
                </button>
              </div>
              )}
              {view === "grid" ? (
                <div className="group/grid flex flex-wrap items-center justify-center gap-x-6 gap-y-8 md:gap-x-4 md:gap-y-10" data-testid="project-overview-grid">
                  {works.map((w, i) => {
                    // Querformat: gleiche Größe wie Hochformat, nur gekippt
                    // (Breite = 1,4 × Spurbreite, Höhe = Spurbreite)
                    const land = w.orientation === "landscape";
                    return (
                    <button
                      key={w.id}
                      type="button"
                      data-testid={`overview-thumb-${i + 1}`}
                      onClick={() => {
                        setIndex(i);
                        setView("slides");
                      }}
                      className={`group text-left ${
                        land
                          ? "w-[calc(50%-0.75rem)] md:w-[calc(23.333%-0.84rem)]"
                          : "w-[calc(50%-0.75rem)] md:w-[calc(16.666%-0.84rem)]"
                      }`}
                    >
                      <img
                        src={thumbUrl(w.image, 800)}
                        srcSet={srcSet(w.image, [400, 800, 1200])}
                        sizes={land ? "(max-width: 768px) 50vw, 23vw" : "(max-width: 768px) 50vw, 17vw"}
                        alt={altFor(w, lang, { name: displayTitle, ort: (lang === "en" ? w.ort_en || "" : w.ort || "") || projectOrt })}
                        loading="lazy"
                        decoding="async"
                        width={land ? 1120 : 800}
                        height={land ? 800 : 1120}
                        className={`w-full object-cover transition-opacity duration-200 group-focus-within/grid:opacity-75 group-hover/grid:opacity-75 group-hover:!opacity-100 group-focus-visible:!opacity-100 ${land ? "aspect-[7/5]" : "aspect-[5/7]"}`}
                      />
                    </button>
                    );
                  })}
                </div>
              ) : (
              <Slideshow
                images={slides}
                index={index}
                onIndexChange={setIndex}
                onOverview={backToOverview}
                overviewLabel={t("Übersicht")}
              />
              )}
            </div>
          )
        )}

        {/* Serienende: nächstes Projekt + Anfrage */}
        {view === "grid" && !loading && works.length > 0 && (
          <div className="mt-20 flex flex-wrap items-center justify-between gap-6 border-t border-black/10 pt-10" data-testid="project-footer-nav">
            {nextProject ? (
              <Link
                to={localizedPath(`/projekte/${nextProject.slug}`, lang)}
                data-testid="project-next-link"
                className="group inline-flex items-baseline gap-4"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-smoke transition-colors duration-300 group-hover:text-ember-deep">
                  {t("Nächstes Projekt")}
                </span>
                <span className="font-serif text-2xl font-light tracking-tight text-bone transition-colors duration-300 group-hover:text-ember md:text-3xl">
                  {nextProject.art && isCategorySeries(nextProject.name)
                    ? t(ART_LABEL[nextProject.art])
                    : lang === "en"
                      ? nextProject.name_en || t(nextProject.name)
                      : nextProject.name}
                </span>
                <ArrowRight size={14} className="self-center text-smoke transition-transform duration-300 group-hover:translate-x-1.5 group-hover:text-ember-deep" />
              </Link>
            ) : (
              <span />
            )}
            <Link
              to={localizedPath("/kontakt", lang)}
              data-testid="project-cta"
              className="inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember-deep"
            >
              {t("Projekt anfragen")} <ArrowRight size={14} />
            </Link>
          </div>
        )}
          </>
        )}
      </section>
    </div>
  );
}
